/* QaInbox (Audit Pool) — sampling QA at /coe/qa.
 *
 * Replaced the legacy QA-gate inbox on 2026-02-28 when qa_audit.py
 * (sampling model) became the source of truth. QA now picks from a pool
 * of planned cases and records non-blocking audit decisions. The doctor
 * never waits on QA — sampling happens out-of-band.
 *
 * Layout:
 *   - 4 stat tiles: Unaudited / Audited / Audited 7d / by-decision
 *   - Audit-status filter chips: Unaudited (default) / Audited / All
 *   - Decision filter chips (visible when viewing Audited): Pass / Concerns / Fail
 *   - Row list — clicking a row opens the case detail page (existing /coe/qa/cases/:id)
 *   - Per-row "Audit" CTA opens the AuditDialog without leaving the list
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    ShieldCheck, ShieldWarning, ShieldSlash, MagnifyingGlass, ArrowRight,
    ClipboardText, Star, Warning,
} from "@phosphor-icons/react";
import AuditDialog from "@/components/qa/AuditDialog";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";


/* Map case status → friendly label + tone. The pool is wider than the
   legacy gate — cases at any post-planning stage are auditable. */
const ROW_STATUS_LABELS = {
    submitted_to_qa:        { label: "Awaiting QA (legacy)", cls: "bg-violet-100 text-violet-800 border-violet-200" },
    sent_for_approval:      { label: "With doctor",          cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
    ready_for_review:       { label: "With doctor",          cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
    approved:               { label: "Approved",             cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    assigned_to_production: { label: "Assigned",             cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    submitted_to_production: { label: "Doctor-planned",      cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
    in_production:          { label: "In production",        cls: "bg-blue-100 text-blue-800 border-blue-200" },
    shipped:                { label: "Shipped",              cls: "bg-orange-100 text-orange-800 border-orange-200" },
    in_treatment:           { label: "In treatment",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    completed:              { label: "Completed",            cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};


const DECISION_TONE = {
    pass:     { label: "Pass",     bg: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: ShieldCheck },
    concerns: { label: "Concerns", bg: "bg-amber-100 text-amber-800 border-amber-200",       Icon: ShieldWarning },
    fail:     { label: "Fail",     bg: "bg-rose-100 text-rose-800 border-rose-200",          Icon: ShieldSlash },
};


export default function QaInbox() {
    const navigate = useNavigate();
    const [pool, setPool] = useState([]);
    const [stats, setStats] = useState(null);
    // iter98.99am — Self-Plan gating queue: cases the admin routed to
    // THIS QA that block production until approved/rejected.
    const [gatingInbox, setGatingInbox] = useState([]);
    const [q, setQ] = useState("");
    const [auditedFilter, setAuditedFilter] = useState("none"); // none | only | all
    const [decisionFilter, setDecisionFilter] = useState(""); // pass | concerns | fail | ""
    const [loading, setLoading] = useState(true);
    const [auditTarget, setAuditTarget] = useState(null);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (auditedFilter !== "none") params.set("audited", auditedFilter);
        if (decisionFilter && auditedFilter !== "none") params.set("decision", decisionFilter);
        Promise.all([
            api.get(`/qa/audit-pool?${params.toString()}`),
            api.get("/qa/audit-pool/stats"),
            // iter98.99am — Pull this QA's Self-Plan gating queue.
            api.get("/qa/inbox").then((r) => r.data).catch(() => []),
        ]).then(([r1, r2, gating]) => {
            setPool(r1.data || []);
            setStats(r2.data || null);
            setGatingInbox(Array.isArray(gating) ? gating : []);
        }).catch(() => {
            // No-op — UI will render empty state
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [auditedFilter, decisionFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        if (!q) return pool;
        const l = q.toLowerCase();
        return pool.filter((c) =>
            (c.patient_name || "").toLowerCase().includes(l) ||
            (c.doctor_name || "").toLowerCase().includes(l) ||
            (c.code || "").toLowerCase().includes(l) ||
            (c.diagnosis || "").toLowerCase().includes(l) ||
            (c.planner_name || "").toLowerCase().includes(l)
        );
    }, [pool, q]);

    return (
        <div data-testid="qa-audit-pool">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold">QA audit pool</h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Sample any planned case and record a non-blocking quality audit.
                        Doctor delivery isn't paused for your decision — your audits feed the planner-quality dashboard and surface failure trends.
                    </p>
                </div>
                <div className="relative w-full max-w-xs">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search case, patient, doctor, planner…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-10"
                        data-testid="qa-pool-search"
                    />
                </div>
            </div>

            {/* iter98.99am — Self-Plan gating queue. Sits at the very top
                of the page because it BLOCKS production for the doctor —
                higher priority than the sampling pool below. Only shows
                when the QA actually has assigned Self-Plan cases. */}
            {gatingInbox.length > 0 && (
                <SelfPlanGatingSection cases={gatingInbox} onChange={load} />
            )}

            {/* Stat tiles */}
            <StatTiles stats={stats} />

            {/* Filter chips */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
                <div className="inline-flex bg-secondary rounded-full p-1" data-testid="qa-audited-filter">
                    {[
                        { k: "none", label: "Unaudited", count: stats?.unaudited },
                        { k: "only", label: "Audited",   count: stats?.audited },
                        { k: "all",  label: "All",       count: stats?.total_eligible },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => { setAuditedFilter(f.k); if (f.k !== "only") setDecisionFilter(""); }}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                auditedFilter === f.k ? "bg-white shadow text-foreground" : "text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`qa-audited-filter-${f.k}`}
                        >
                            {f.label}{f.count != null && <span className="ml-1 opacity-75">· {f.count}</span>}
                        </button>
                    ))}
                </div>

                {/* Decision filter — only meaningful when viewing audited */}
                {auditedFilter === "only" && (
                    <div className="inline-flex bg-secondary rounded-full p-1" data-testid="qa-decision-filter">
                        {[
                            { k: "",         label: "All decisions" },
                            { k: "pass",     label: "Pass",     count: stats?.by_decision?.pass },
                            { k: "concerns", label: "Concerns", count: stats?.by_decision?.concerns },
                            { k: "fail",     label: "Fail",     count: stats?.by_decision?.fail },
                        ].map((f) => {
                            const tone = DECISION_TONE[f.k];
                            const active = decisionFilter === f.k;
                            return (
                                <button
                                    key={f.k || "all"}
                                    onClick={() => setDecisionFilter(f.k)}
                                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                        active
                                            ? f.k === "pass" ? "bg-emerald-600 text-white"
                                                : f.k === "concerns" ? "bg-amber-500 text-white"
                                                : f.k === "fail" ? "bg-rose-600 text-white"
                                                : "bg-white shadow text-foreground"
                                            : "text-muted-foreground hover:text-foreground"
                                    }`}
                                    data-testid={`qa-decision-filter-${f.k || "all"}`}
                                    disabled={f.count === 0 && f.k}
                                >
                                    {tone && <tone.Icon size={10} weight="fill" className="inline mr-1" />}
                                    {f.label}{f.count != null && <span className="ml-1 opacity-75">· {f.count}</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {loading && <div className="text-muted-foreground" data-testid="qa-pool-loading">Loading audit pool…</div>}

            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center" data-testid="qa-pool-empty">
                    <ShieldCheck size={32} className="mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                        {q ? "No cases match your search."
                            : auditedFilter === "none" ? "All planned cases have been audited. Nice."
                            : auditedFilter === "only" ? "No audits recorded yet under this filter."
                            : "No cases in the pool."}
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {filtered.map((c) => (
                    <PoolRow
                        key={c.id}
                        c={c}
                        onOpen={() => navigate(`/coe/qa/cases/${c.id}`)}
                        onAudit={() => setAuditTarget(c)}
                    />
                ))}
            </div>

            <AuditDialog
                open={!!auditTarget}
                caseObj={auditTarget}
                onClose={() => setAuditTarget(null)}
                onSubmitted={() => { setAuditTarget(null); load(); }}
            />
        </div>
    );
}


function StatTiles({ stats }) {
    const tiles = [
        { k: "unaudited",     label: "Unaudited",      value: stats?.unaudited ?? "—",   color: "hsl(280 60% 55%)", icon: ClipboardText, testid: "qa-stat-unaudited" },
        { k: "audited",       label: "Audited",        value: stats?.audited ?? "—",     color: "hsl(150 60% 40%)", icon: ShieldCheck,   testid: "qa-stat-audited" },
        { k: "audited_7d",    label: "Audited (7 days)", value: stats?.audited_last_7d ?? "—", color: "hsl(220 80% 50%)", icon: Star,    testid: "qa-stat-7d" },
        { k: "fail_count",    label: "Failed audits",  value: stats?.by_decision?.fail ?? "—", color: "hsl(0 75% 50%)",  icon: ShieldSlash, testid: "qa-stat-fail" },
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {tiles.map((t) => (
                <div key={t.k} className="p-4 bg-card border border-border rounded-2xl" data-testid={t.testid}>
                    <t.icon size={20} weight="duotone" color={t.color} />
                    <div className="font-display text-2xl font-bold mt-2">{t.value}</div>
                    <div className="text-xs text-muted-foreground">{t.label}</div>
                </div>
            ))}
        </div>
    );
}


function PoolRow({ c, onOpen, onAudit }) {
    const status = ROW_STATUS_LABELS[c.status] || { label: c.status?.replace(/_/g, " ") || "—", cls: "bg-secondary text-muted-foreground border-border" };
    const latest = c.latest_audit;
    const auditCount = c.audits_count || 0;
    const latestTone = latest ? DECISION_TONE[latest.decision] : null;
    const ts = c.status_updated_at ? new Date(c.status_updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";
    return (
        <div
            className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4 hover:border-primary/40 transition-all"
            data-testid={`qa-pool-case-${c.id}`}
        >
            <button
                onClick={onOpen}
                className="flex-1 flex items-center gap-4 text-left min-w-0"
                data-testid={`qa-pool-open-${c.id}`}
            >
                <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                    <ShieldCheck size={20} weight="duotone" color="hsl(280 60% 55%)" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium truncate">{c.patient_name || "Patient"}</div>
                        {c.code && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{c.code}</span>}
                        {c.case_kind === "refinement" && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                Refinement
                            </span>
                        )}
                        {c.case_kind === "retainer" && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200">
                                Retainer
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                        Dr. {(c.doctor_name || "—").replace(/^Dr\.\s*/, "")}
                        {c.planner_name ? <> · Planned by {c.planner_name}</> : null}
                        {c.diagnosis ? <> · {c.diagnosis}</> : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${status.cls}`}>
                            {status.label}
                        </span>
                        <span className="text-[10px] text-muted-foreground/80">Updated {ts}</span>
                        {latest && latestTone && (
                            <span
                                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${latestTone.bg}`}
                                title={latest.note}
                                data-testid={`qa-pool-latest-${c.id}`}
                            >
                                <latestTone.Icon size={10} weight="fill" />
                                {latestTone.label}
                                {latest.score && <span className="ml-0.5 opacity-75">· {latest.score}/5</span>}
                            </span>
                        )}
                        {auditCount > 1 && (
                            <span className="text-[10px] text-muted-foreground">
                                +{auditCount - 1} prior
                            </span>
                        )}
                        {Array.isArray(latest?.flags) && latest.flags.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700">
                                <Warning size={9} weight="bold" />
                                {latest.flags.length} flag{latest.flags.length === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>
                </div>
                <ArrowRight size={14} className="text-muted-foreground shrink-0" />
            </button>
            <Button
                size="sm"
                onClick={onAudit}
                className="rounded-full shrink-0"
                data-testid={`qa-pool-audit-${c.id}`}
            >
                <ShieldCheck size={12} className="mr-1.5" weight="fill" /> Audit
            </Button>
        </div>
    );
}


/* iter98.99am — Self-Plan gating section.
 *
 * Shown above the audit pool when the QA has assigned Self-Plan cases
 * waiting on their decision. Doctor + admin have routed the case here;
 * the QA is the ONLY person who can move it to production by approving
 * (or bounce it back with a note by rejecting).
 *
 * Approve: PUT /api/qa/cases/{id}/approve → status flips to
 *          `assigned_to_production` (Self-Plan branch in qa_approve_case).
 * Reject:  PUT /api/qa/cases/{id}/reject  → status flips to `info_requested`
 *          + the note is captured on the case so the doctor sees what
 *          QA needs clarified.
 */
function SelfPlanGatingSection({ cases, onChange }) {
    const navigate = useNavigate();
    const [busyId, setBusyId] = useState(null);
    const [rejectFor, setRejectFor] = useState(null);
    const [rejectNote, setRejectNote] = useState("");
    /* iter98.99i — Premium "QA cleared · sent to doctor" confirmation
     * dialog. Replaces the (stale + incorrect) "routed to production"
     * toast — Phase 2 billing routes QA-approved Self-Plan cases back
     * to the doctor for final sign-off, not directly to production. */
    const [approvedFor, setApprovedFor] = useState(null);

    const approve = async (c) => {
        setBusyId(c.id);
        try {
            await api.put(`/qa/cases/${c.id}/approve`, { note: "" });
            setApprovedFor(c);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve");
        } finally {
            setBusyId(null);
        }
    };

    const submitReject = async () => {
        if (!rejectFor) return;
        if (!rejectNote.trim()) {
            toast.error("Please add a note for the doctor");
            return;
        }
        setBusyId(rejectFor.id);
        try {
            await api.put(`/qa/cases/${rejectFor.id}/reject`, { note: rejectNote.trim() });
            toast.success(`${rejectFor.code || rejectFor.id?.slice(0, 8)} returned to doctor`);
            setRejectFor(null);
            setRejectNote("");
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="qa-self-plan-gating-section">
            <div className="px-5 py-3 border-b border-amber-200/70 flex items-center gap-2">
                <ShieldWarning size={16} weight="fill" className="text-amber-700" />
                <span className="font-display text-sm font-bold uppercase tracking-[0.12em] text-amber-900">
                    Awaiting your decision · Self-Plan cases
                </span>
                <span className="ml-auto text-[10.5px] font-bold uppercase tracking-wider text-amber-800/90 bg-amber-100 px-2 py-0.5 rounded-full">
                    {cases.length}
                </span>
            </div>
            <div className="divide-y divide-amber-200/60">
                {cases.map((c) => (
                    <div
                        key={c.id}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-amber-100/30 transition-colors"
                        data-testid={`qa-self-plan-row-${c.id}`}
                    >
                        <button
                            type="button"
                            onClick={() => navigate(`/coe/qa/cases/${c.id}`)}
                            className="flex-1 text-left min-w-0"
                        >
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-[12px] font-bold text-foreground">{c.code || c.id?.slice(0, 8)}</span>
                                <span className="text-[12.5px] text-foreground/85 truncate">{c.patient_name || "—"}</span>
                            </div>
                            <div className="text-[10.5px] text-muted-foreground mt-0.5 truncate">
                                Dr. {c.doctor_name || "—"}{c.diagnosis ? ` · ${c.diagnosis}` : ""}
                            </div>
                        </button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRejectFor(c)}
                            disabled={busyId === c.id}
                            className="h-8 rounded-full border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold"
                            data-testid={`qa-self-plan-reject-${c.id}`}
                        >
                            <ShieldSlash size={11} className="mr-1" weight="fill" />
                            Return
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => approve(c)}
                            disabled={busyId === c.id}
                            className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                            data-testid={`qa-self-plan-approve-${c.id}`}
                        >
                            <ShieldCheck size={11} className="mr-1" weight="fill" />
                            Approve
                        </Button>
                    </div>
                ))}
            </div>

            {/* Reject-with-note dialog */}
            {rejectFor && (
                <div
                    className="fixed inset-0 z-50 grid place-items-center bg-foreground/30 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) { setRejectFor(null); setRejectNote(""); } }}
                    data-testid="qa-self-plan-reject-dialog"
                >
                    <div className="bg-card border border-border rounded-2xl p-5 max-w-md w-full shadow-2xl">
                        <h3 className="font-display text-base font-bold mb-1">Return case to doctor</h3>
                        <p className="text-[12px] text-muted-foreground mb-3">
                            Add a note explaining what the doctor needs to clarify. They&apos;ll see this on their case page and can respond once they&apos;ve fixed the issue.
                        </p>
                        <textarea
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="e.g. STL files appear corrupted — please re-upload the upper scan."
                            rows={4}
                            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                            data-testid="qa-self-plan-reject-note"
                        />
                        <div className="mt-3 flex justify-end gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setRejectFor(null); setRejectNote(""); }}
                                className="rounded-full"
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={submitReject}
                                disabled={!rejectNote.trim() || busyId === rejectFor.id}
                                className="rounded-full bg-rose-600 hover:bg-rose-700"
                                data-testid="qa-self-plan-reject-confirm"
                            >
                                {busyId === rejectFor.id ? "Returning…" : "Return to doctor"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* iter98.99i — Premium "Plan sent to doctor for approval"
                confirmation. Replaces the legacy thin toast. Closing the
                dialog refreshes the inbox via onChange so the row drops
                out of the gating section. */}
            <CaseSubmittedDialog
                open={!!approvedFor}
                flow="qa_sent_to_doctor"
                caseCode={approvedFor?.code || ""}
                primaryLabel="Back to inbox"
                onPrimary={() => { setApprovedFor(null); onChange?.(); }}
            />
        </div>
    );
}
